import React, { useState, useEffect, useMemo } from "react";
import {
  Table,
  Pagination,
  Flex,
  Text,
  Stack,
  ActionIcon,
  Tooltip,
  Button,
  Menu,
} from "@mantine/core";
import { PageContainer } from "../components/Container/PageContainer";
import {
  IconBookmark,
  IconBookmarkFilled,
  IconDownload,
  IconFileCode,
  IconChevronDown,
} from "@tabler/icons-react";
import DataLoader from "../components/DataLoader";
import { useTranslation } from "react-i18next";
import { trackEvent } from "../utils/analytics";
import { useIsMobile } from "../hooks/useIsMobile";
import { useUser } from "../contexts/UserContext";
import { dbService } from "../firebaseInstance";

const Bookmarks = () => {
  const { profile } = useUser();
  const [bookmarkedThreads, setBookmarkedThreads] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isBookmarkLoading, setIsBookmarkLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const threadsPerPage = 30;
  const [sortConfig, setSortConfig] = useState({
    key: "bookmarkedAt",
    direction: "desc",
  });
  const isMobile = useIsMobile("xl");
  const { t } = useTranslation();

  const handleDownloadCSV = () => {
    if (!bookmarkedThreads || bookmarkedThreads.length === 0) {
      console.log("No data to download.");
      return;
    }

    // Define CSV Headers
    const headers = [
      "Date",
      "Content",
      "Views",
      "Likes",
      "Replies",
      "Reposts",
      "ER (%)", // Engagement Rate
      "EA", // Engagement Amount
      "Link",
      "Bookmarked At",
    ];
    const csvRows = [headers.join(",")];

    // Create CSV rows from bookmarkedThreads data
    bookmarkedThreads.forEach((thread) => {
      const timestamp = new Date(thread.timestamp).toLocaleDateString();
      const textContent = `"${thread.text
        .replace(/"/g, '""')
        .replace(/\uFFFD/g, "")}"`;
      const views = thread.views || 0;
      const likes = thread.likes || 0;
      const replies = thread.replies || 0;
      const reposts = thread.reposts || 0;
      const engagementRate = views
        ? (((likes + replies + reposts) / views) * 100).toFixed(2)
        : "0.00";
      const engagementAmount = likes + replies + reposts;
      const permalink = thread.permalink || "";
      const bookmarkedAt = thread.bookmarkedAt
        ? new Date(thread.bookmarkedAt).toLocaleDateString()
        : "";

      const row = [
        timestamp,
        textContent,
        views,
        likes,
        replies,
        reposts,
        engagementRate,
        engagementAmount,
        permalink,
        bookmarkedAt,
      ].join(",");
      csvRows.push(row);
    });

    const csvString = csvRows.join("\n");
    const BOM = "\uFEFF";
    const blob = new Blob([BOM + csvString], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", "bookmarked_threads_export.csv");
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    trackEvent("download_bookmarks_csv", { count: bookmarkedThreads.length });
    console.log("CSV download triggered.");
  };

  const handleDownloadJSON = () => {
    if (!bookmarkedThreads || bookmarkedThreads.length === 0) {
      console.log("No data to download.");
      return;
    }

    // Prepare data for JSON export
    const jsonData = bookmarkedThreads.map((thread) => {
      const views = thread.views || 0;
      const likes = thread.likes || 0;
      const replies = thread.replies || 0;
      const reposts = thread.reposts || 0;
      const engagementRate = views
        ? parseFloat((((likes + replies + reposts) / views) * 100).toFixed(2))
        : 0;
      const engagementAmount = likes + replies + reposts;

      return {
        date: new Date(thread.timestamp).toLocaleDateString(),
        content: thread.text.replace(/\uFFFD/g, ""),
        views: views,
        likes: likes,
        replies: replies,
        reposts: reposts,
        engagementRate: engagementRate,
        engagementAmount: engagementAmount,
        link: thread.permalink || "",
        bookmarkedAt: thread.bookmarkedAt || "",
      };
    });

    const jsonString = JSON.stringify(jsonData, null, 2);
    const BOM = "\uFEFF";
    const blob = new Blob([BOM + jsonString], {
      type: "application/json;charset=utf-8;",
    });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", "bookmarked_threads_export.json");
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    trackEvent("download_bookmarks_json", { count: bookmarkedThreads.length });
    console.log("JSON download triggered.");
  };

  // Load bookmarks from Firestore subcollection
  useEffect(() => {
    const loadBookmarks = async () => {
      if (!profile?.id) return;

      setIsLoading(true);
      try {
        const bookmarksSnapshot = await dbService
          .collection("user")
          .doc(profile.id)
          .collection("bookmarkedThreads")
          .get();

        const bookmarks = bookmarksSnapshot.docs.map(doc => ({
          ...doc.data(),
          id: doc.id,
        }));

        setBookmarkedThreads(bookmarks);
      } catch (error) {
        console.error("Failed to load bookmarks:", error);
      } finally {
        setIsLoading(false);
      }
    };

    loadBookmarks();
  }, [profile?.id]);

  // Remove bookmark
  const removeBookmark = async (threadId) => {
    if (!profile?.id || isBookmarkLoading) return;

    setIsBookmarkLoading(true);
    try {
      await dbService
        .collection("user")
        .doc(profile.id)
        .collection("bookmarkedThreads")
        .doc(threadId)
        .delete();

      setBookmarkedThreads(prev => prev.filter(thread => thread.id !== threadId));
      trackEvent("remove_bookmark_from_page", { threadId });
    } catch (error) {
      console.error("Failed to remove bookmark:", error);
    } finally {
      setIsBookmarkLoading(false);
    }
  };

  // Sort threads
  const sortThreads = (threads) => {
    if (!sortConfig.key) return threads;

    return [...threads].sort((a, b) => {
      if (sortConfig.key === "timestamp" || sortConfig.key === "bookmarkedAt") {
        const timestampA = new Date(a[sortConfig.key]).getTime();
        const timestampB = new Date(b[sortConfig.key]).getTime();
        return sortConfig.direction === "asc"
          ? timestampA - timestampB
          : timestampB - timestampA;
      }
      if (
        sortConfig.key === "engagementRate" ||
        sortConfig.key === "engagementAmount"
      ) {
        const valA =
          sortConfig.key === "engagementRate"
            ? ((a.likes + a.replies + a.reposts) / a.views) * 100
            : a.likes + a.replies + a.reposts;
        const valB =
          sortConfig.key === "engagementRate"
            ? ((b.likes + b.replies + b.reposts) / b.views) * 100
            : b.likes + b.replies + b.reposts;
        return sortConfig.direction === "asc" ? valA - valB : valB - valA;
      }
      if (sortConfig.direction === "asc") {
        return a[sortConfig.key] - b[sortConfig.key];
      }
      return b[sortConfig.key] - a[sortConfig.key];
    });
  };

  const handleSort = (key) => {
    setSortConfig((current) => ({
      key,
      direction:
        current.key === key && current.direction === "asc" ? "desc" : "asc",
    }));
    trackEvent("sort_bookmarks_table", { key: key });
  };

  const sortedThreads = useMemo(() => {
    return sortThreads(bookmarkedThreads);
  }, [bookmarkedThreads, sortConfig]);

  const indexOfLastThread = currentPage * threadsPerPage;
  const indexOfFirstThread = indexOfLastThread - threadsPerPage;
  const currentThreads = sortedThreads.slice(
    indexOfFirstThread,
    indexOfLastThread
  );

  const columns = [
    { key: "views", label: t("searchThreads.tableColumns.views") },
    {
      key: "likes",
      label: t("searchThreads.tableColumns.likes"),
      mobileHidden: true,
    },
    {
      key: "replies",
      label: t("searchThreads.tableColumns.replies"),
      mobileHidden: true,
    },
    {
      key: "reposts",
      label: t("searchThreads.tableColumns.reposts"),
      mobileHidden: true,
    },
    {
      key: "engagementRate",
      label: t("searchThreads.tableColumns.engagementRate"),
      mobileHidden: true,
    },
    {
      key: "engagementAmount",
      label: t("searchThreads.tableColumns.engagementAmount"),
      mobileHidden: true,
    },
  ];

  return (
    <PageContainer>
      <DataLoader msg="Loading bookmarks..." isLoad={isLoading} />
      <Stack gap="sm">
        <Flex direction="row" justify="space-between" align="center" gap="md">
          <Flex direction="row" align="center" gap="md">
            <Text size="xl" fw={700}>
              Bookmarked Threads
            </Text>
            <Text size="sm" c="dimmed">
              {sortedThreads.length} bookmark{sortedThreads.length !== 1 ? 's' : ''}
            </Text>
          </Flex>
          <Menu shadow="md" width={200}>
            <Menu.Target>
              <Button
                leftSection={<IconDownload size={18} />}
                rightSection={<IconChevronDown size={16} />}
                disabled={!bookmarkedThreads || bookmarkedThreads.length === 0}
                size="md"
                color="inform.9"
              >
                Download
              </Button>
            </Menu.Target>
            <Menu.Dropdown>
              <Menu.Item
                leftSection={<IconDownload size={16} />}
                onClick={handleDownloadCSV}
              >
                {t("searchThreads.terminology.downloadCsv")}
              </Menu.Item>
              <Menu.Item
                leftSection={<IconFileCode size={16} />}
                onClick={handleDownloadJSON}
              >
                {t("searchThreads.terminology.downloadJson")}
              </Menu.Item>
            </Menu.Dropdown>
          </Menu>
        </Flex>

        {currentThreads.length === 0 ? (
          <Text c="dimmed" ta="center" py="xl">
            {isLoading
              ? "Loading bookmarked threads..."
              : "No bookmarked threads yet. Start bookmarking threads from the Search page!"}
          </Text>
        ) : (
          <>
            <Table highlightOnHover fz={isMobile ? "md" : "lg"} withTableBorder={false} withColumnBorders={false} withRowBorders={false} style={{ borderRadius: "8px", overflow: "hidden" }}>
              <Table.Thead style={{
                display: "block",
                width: "100%",
                scrollbarGutter: "stable",
                backgroundColor: "var(--mantine-color-inform-9)",
                color: "white"
              }}>
                <Table.Tr style={{ display: "flex", width: "100%" }}>
                  <Table.Th
                    p={isMobile ? 8 : 16}
                    style={{ flex: "0 0 60px", color: "white", textAlign: "center" }}
                  >
                    <IconBookmark size={20} />
                  </Table.Th>
                  <Table.Th
                    p={isMobile ? 8 : 16}
                    style={{ flex: "0 0 120px", cursor: "pointer", color: "white" }}
                    onClick={() => handleSort("timestamp")}
                  >
                    <Flex direction="row" align="center">
                      {t("searchThreads.tableColumns.date")}
                      {sortConfig.key === "timestamp" && (
                        <span>
                          {sortConfig.direction === "asc" ? " ↑" : " ↓"}
                        </span>
                      )}
                    </Flex>
                  </Table.Th>
                  <Table.Th p={isMobile ? 8 : 16} style={{ flex: "1", color: "white" }}>
                    <Flex direction="row" align="center">
                      {t("searchThreads.tableColumns.content")}
                    </Flex>
                  </Table.Th>
                  {columns.map((col) =>
                    !isMobile || !col.mobileHidden ? (
                      <Table.Th
                        key={col.key}
                        p={isMobile ? 8 : 16}
                        style={{ flex: "0 0 100px", cursor: "pointer", color: "white" }}
                        onClick={() => handleSort(col.key)}
                      >
                        <Flex direction="row" align="center">
                          {col.label}
                          {sortConfig.key === col.key && (
                            <span>
                              {sortConfig.direction === "asc" ? " ↑" : " ↓"}
                            </span>
                          )}
                        </Flex>
                      </Table.Th>
                    ) : null
                  )}
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody
                style={{
                  display: "block",
                  overflow: "auto",
                  height: isMobile
                    ? "calc(100vh - 280px - 24px)"
                    : "calc(100vh - 280px - 48px)",
                  width: "100%",
                  backgroundColor: "white"
                }}
              >
                {currentThreads.map((thread) => (
                  <Table.Tr
                    key={thread.id}
                    style={{ display: "flex", width: "100%" }}
                  >
                    <Table.Td
                      p={isMobile ? 8 : 16}
                      style={{ flex: "0 0 60px", textAlign: "center" }}
                    >
                      <Tooltip label="Remove bookmark">
                        <ActionIcon
                          variant="subtle"
                          color="yellow.6"
                          onClick={() => removeBookmark(thread.id)}
                          disabled={isBookmarkLoading}
                          size="md"
                        >
                          <IconBookmarkFilled size={20} />
                        </ActionIcon>
                      </Tooltip>
                    </Table.Td>
                    <Table.Td
                      p={isMobile ? 8 : 16}
                      style={{ flex: "0 0 120px" }}
                    >
                      {new Date(thread.timestamp).toLocaleDateString()}
                    </Table.Td>
                    <Table.Td p={isMobile ? 8 : 16} style={{ flex: "1" }}>
                      <a
                        href={thread.permalink}
                        target="_blank"
                        rel="noreferrer"
                        style={{
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {thread.text?.split(".")[0]?.replace(/�/g, "") || "No content"}
                      </a>
                    </Table.Td>
                    <Table.Td
                      p={isMobile ? 8 : 16}
                      style={{ flex: "0 0 100px" }}
                    >
                      {thread.views || 0}
                    </Table.Td>
                    {!isMobile && (
                      <>
                        <Table.Td
                          p={isMobile ? 8 : 16}
                          style={{ flex: "0 0 100px" }}
                        >
                          {thread.likes || 0}
                        </Table.Td>
                        <Table.Td
                          p={isMobile ? 8 : 16}
                          style={{ flex: "0 0 100px" }}
                        >
                          {thread.replies || 0}
                        </Table.Td>
                        <Table.Td
                          p={isMobile ? 8 : 16}
                          style={{ flex: "0 0 100px" }}
                        >
                          {thread.reposts || 0}
                        </Table.Td>
                        <Table.Td
                          p={isMobile ? 8 : 16}
                          style={{ flex: "0 0 100px" }}
                        >
                          {thread.views && thread.views > 0
                            ? (
                                ((thread.likes + thread.replies + thread.reposts) /
                                  thread.views) *
                                100
                              ).toFixed(2)
                            : "0.00"}
                          %
                        </Table.Td>
                        <Table.Td
                          p={isMobile ? 8 : 16}
                          style={{ flex: "0 0 100px" }}
                        >
                          {(thread.likes || 0) + (thread.replies || 0) + (thread.reposts || 0)}
                        </Table.Td>
                      </>
                    )}
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
            <Flex justify="center" w="100%">
              <Pagination
                size="sm"
                total={Math.ceil(sortedThreads.length / threadsPerPage)}
                value={currentPage}
                onChange={setCurrentPage}
              />
            </Flex>
          </>
        )}
      </Stack>
    </PageContainer>
  );
};

export default Bookmarks;
